import axios from 'axios';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { toast } from 'sonner';
import { API_BASE, IS_NATIVE, NATIVE_PLATFORM } from '../config/runtime';
import { NOTIFICATIONS_REFRESH_EVENT } from './supervisionSync';

/**
 * Push on the store app (Firebase Cloud Messaging on both platforms).
 *
 * The phone is only asked for notification permission once the server says
 * push is actually on for this company (`/push/status`): a permission prompt
 * for messages that will never arrive teaches people to say no to the one that
 * matters. The device token is registered against whoever is signed in, and
 * withdrawn at sign-out so a shared phone stops receiving the last person's
 * approvals.
 */

const TOKEN_KEY = 'erp_push_token_v1';
// Matches com.google.firebase.messaging.default_notification_channel_id in
// AndroidManifest.xml, so server pushes land in a channel the app named.
const ANDROID_CHANNEL = 'erp_alerts';

let listenersInstalled = false;

function withTimeout(promise, ms) {
  return Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))]);
}

async function sendToken(token) {
  if (!token || !localStorage.getItem('token')) return;
  await axios.post(`${API_BASE}/push/devices`, {
    token,
    platform: NATIVE_PLATFORM === 'ios' ? 'ios' : 'android',
    app_version: process.env.REACT_APP_VERSION || null,
  });
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
}

/** Register this phone for the signed-in user. Safe to call on every launch and login. */
export async function registerPush() {
  if (!IS_NATIVE || !localStorage.getItem('token')) return;
  try {
    const { data } = await axios.get(`${API_BASE}/push/status`);
    if (!data?.available) return;

    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await FirebaseMessaging.requestPermissions();
    }
    if (perm.receive !== 'granted') return;

    if (NATIVE_PLATFORM === 'android') {
      await FirebaseMessaging.createChannel({
        id: ANDROID_CHANNEL, name: 'Alerts', description: 'Approvals, holds and alerts from Aqua ERP', importance: 4, visibility: 1,
      }).catch(() => {});
    }
    // A build without the Firebase config files cannot produce a token; the
    // native call may then never answer, hence the timeout.
    const { token } = await withTimeout(FirebaseMessaging.getToken(), 15000);
    await sendToken(token);
  } catch {
    /* push is a convenience — never let it break sign-in */
  }
}

/** Withdraw this phone from the user signing out. Call before the session token is cleared. */
export function unregisterPush() {
  if (!IS_NATIVE) return;
  let token = null;
  try { token = localStorage.getItem(TOKEN_KEY); } catch { /* ignore */ }
  const session = localStorage.getItem('token');
  if (!token || !session) return;
  axios.post(`${API_BASE}/push/devices/unregister`, { token }, { headers: { Authorization: `Bearer ${session}` } })
    .catch(() => {});
}

/**
 * Tap on a push → open the screen it is about. A push that arrives while the
 * app is open shows as a toast instead (the system shows nothing then) and
 * refreshes the bell.
 */
export function installPushListeners(navigate) {
  if (!IS_NATIVE || listenersInstalled) return;
  listenersInstalled = true;

  FirebaseMessaging.addListener('tokenReceived', ({ token }) => { sendToken(token).catch(() => {}); });

  FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) => {
    const link = notification?.data?.link;
    navigate(typeof link === 'string' && link.startsWith('/') ? link : '/notifications');
  });

  FirebaseMessaging.addListener('notificationReceived', ({ notification }) => {
    const link = notification?.data?.link;
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESH_EVENT));
    toast(notification?.title || 'Aqua ERP', {
      description: notification?.body || undefined,
      action: typeof link === 'string' && link.startsWith('/')
        ? { label: 'Open', onClick: () => navigate(link) }
        : undefined,
    });
  });
}
